/** Admin: System Operations (outbox, mail, release policy, cohorts, rate limits, feature flags, model config, leases, health). */
import { z } from 'zod';
import { LeaseIdSchema, MailDeliveryIdSchema, OutboxItemIdSchema, ProviderSchema, UserIdSchema } from '../../ids.js';
import { CountSchema, CursorQuerySchema, EmptyRequestSchema, IsoDateTimeSchema, NoQuerySchema, RevisionSchema, SemVerSchema, defineRoute, pageOf } from '../common.js';
import { CohortSchema, ProviderLeaseSchema, ReleasePolicySchema } from '../entities.js';

export const OUTBOX_KINDS = ['notification', 'mail', 'archive_partition', 'usage_reconciliation', 'provider_revocation', 'housekeeping'] as const;
export const OutboxKindSchema = z.enum(OUTBOX_KINDS);
export const OUTBOX_STATES = ['pending', 'in_progress', 'succeeded', 'failed', 'dead'] as const;
export const OutboxStateSchema = z.enum(OUTBOX_STATES);

export const OutboxItemSchema = z.object({
  id: OutboxItemIdSchema,
  kind: OutboxKindSchema,
  state: OutboxStateSchema,
  idempotencyKey: z.string(),
  attempts: CountSchema,
  maxAttempts: CountSchema,
  nextAttemptAt: IsoDateTimeSchema.nullable(),
  lastError: z.string().max(300).nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  /** Allowlisted summary (kind-specific ids only). */
  summary: z.record(z.string(), z.string()),
});
export type OutboxItem = z.infer<typeof OutboxItemSchema>;

export const OutboxListQuerySchema = CursorQuerySchema.extend({ kind: OutboxKindSchema.optional(), state: OutboxStateSchema.optional() });
export const OutboxPageSchema = pageOf(OutboxItemSchema).extend({ counts: z.record(OutboxStateSchema, CountSchema) });
export const OutboxParamsSchema = z.object({ itemId: OutboxItemIdSchema });

export const MAIL_KINDS = ['invitation', 'access_change', 'device_registered', 'update_required', 'template_published', 'operational_failure', 'summary', 'test', 'milestone'] as const;
export const MailKindSchema = z.enum(MAIL_KINDS);
export const MAIL_STATES = ['queued', 'sent', 'delivered', 'bounced', 'failed'] as const;
export const MailStateSchema = z.enum(MAIL_STATES);

export const MailDeliverySchema = z.object({
  id: MailDeliveryIdSchema,
  kind: MailKindSchema,
  recipientUserId: UserIdSchema.nullable(),
  /** Masked recipient (l***@agency.example). */
  recipientMasked: z.string(),
  templateVersion: z.string(),
  state: MailStateSchema,
  attempts: CountSchema,
  providerMessageId: z.string().nullable(),
  lastError: z.string().max(300).nullable(),
  createdAt: IsoDateTimeSchema,
  sentAt: IsoDateTimeSchema.nullable(),
  /** Local mode: link to the sim mail viewer. */
  viewerUrl: z.string().url().nullable(),
});
export type MailDelivery = z.infer<typeof MailDeliverySchema>;

export const MailListQuerySchema = CursorQuerySchema.extend({ kind: MailKindSchema.optional(), state: MailStateSchema.optional(), recipientUserId: UserIdSchema.optional() });
export const MailPageSchema = pageOf(MailDeliverySchema);
export const MailParamsSchema = z.object({ deliveryId: MailDeliveryIdSchema });

export const ReleasePolicyUpdateRequestSchema = z
  .object({
    minAppVersion: SemVerSchema.optional(),
    currentAppVersion: SemVerSchema.optional(),
    approvedBuildIds: z.array(z.string().max(128)).optional(),
    stage: ReleasePolicySchema.shape.stage.optional(),
    stageBuildIds: ReleasePolicySchema.shape.stageBuildIds.optional(),
    channel: ReleasePolicySchema.shape.channel.optional(),
    revision: RevisionSchema,
  })
  .strict();
export type ReleasePolicyUpdateRequest = z.infer<typeof ReleasePolicyUpdateRequestSchema>;

export const CohortsUpdateRequestSchema = z.object({ cohorts: z.array(CohortSchema), revision: RevisionSchema }).strict();
export const ReleasePolicyResponseSchema = z.object({ policy: ReleasePolicySchema });

/** Versioned limiter configuration (spec section 6: limits are configuration, not constants). */
export const RateLimitBucketSchema = z.object({
  requestsPerMinute: z.number().int().positive(),
  burst: z.number().int().positive(),
  concurrency: z.number().int().positive(),
});
export const RateLimitConfigSchema = z.object({
  version: z.number().int().positive(),
  providers: z.record(
    ProviderSchema,
    z.object({
      ceiling: RateLimitBucketSchema,
      auth: RateLimitBucketSchema,
      perEndpointFamily: z.record(z.string(), RateLimitBucketSchema),
      egressGroupCeilingPerMinute: z.number().int().positive().nullable(),
    }),
  ),
  retry: z.object({
    maxRetries: z.number().int().min(0).max(10),
    perRequestBudgetMs: z.number().int().positive(),
    baseDelayMs: z.number().int().positive(),
    maxDelayMs: z.number().int().positive(),
    connectTimeoutMs: z.number().int().positive(),
    responseTimeoutMs: z.number().int().positive(),
  }),
  updatedAt: IsoDateTimeSchema,
  updatedByUserId: UserIdSchema.nullable(),
  revision: RevisionSchema,
});
export type RateLimitConfig = z.infer<typeof RateLimitConfigSchema>;

export const RateLimitConfigUpdateRequestSchema = RateLimitConfigSchema.omit({ version: true, updatedAt: true, updatedByUserId: true }).strict();

export const FeatureFlagsSchema = z.object({
  version: z.number().int().positive(),
  flags: z.record(z.string().regex(/^[a-z][a-z0-9_]{1,63}$/), z.union([z.boolean(), z.number(), z.string().max(200)])),
  /** Per-account Rumi disablement (spec section 11). */
  rumiDisabledAccountIds: z.array(z.string()),
  updatedAt: IsoDateTimeSchema,
  updatedByUserId: UserIdSchema.nullable(),
  revision: RevisionSchema,
});
export type FeatureFlags = z.infer<typeof FeatureFlagsSchema>;
export const FeatureFlagsUpdateRequestSchema = FeatureFlagsSchema.omit({ version: true, updatedAt: true, updatedByUserId: true }).strict();

export const ModelConfigSchema = z.object({
  version: z.number().int().positive(),
  defaultModel: z.string().min(1),
  higherCapabilityModel: z.string().nullable(),
  permittedModels: z.array(z.string().min(1)).min(1),
  maxOutputTokens: z.number().int().positive(),
  reasoningEffort: z.enum(['minimal', 'low', 'medium', 'high']).nullable(),
  temperature: z.number().min(0).max(2).nullable(),
  /** Dated rate table: integer micro-USD per 1M tokens. */
  priceTable: z.object({
    version: z.string().min(1),
    effectiveFrom: IsoDateTimeSchema,
    models: z.record(
      z.string(),
      z.object({ inputMicroUsdPerMillion: z.number().int().nonnegative(), cachedInputMicroUsdPerMillion: z.number().int().nonnegative(), outputMicroUsdPerMillion: z.number().int().nonnegative() }),
    ),
  }),
  perUserBudgetMicroUsdPerMonth: z.number().int().nonnegative().nullable(),
  maxReservationMicroUsd: z.number().int().positive(),
  baseUrl: z.string().url(),
  policyVersion: z.string().min(1),
  updatedAt: IsoDateTimeSchema,
  updatedByUserId: UserIdSchema.nullable(),
  revision: RevisionSchema,
});
export type ModelConfig = z.infer<typeof ModelConfigSchema>;
export const ModelConfigUpdateRequestSchema = ModelConfigSchema.omit({ version: true, updatedAt: true, updatedByUserId: true }).strict();

export const LeaseListQuerySchema = CursorQuerySchema.extend({ provider: ProviderSchema.optional(), activeOnly: z.coerce.boolean().default(true) });
export const LeasePageSchema = pageOf(ProviderLeaseSchema.extend({ ownerDisplayName: z.string(), ownerDeviceName: z.string() }));
export const LeaseParamsSchema = z.object({ leaseId: LeaseIdSchema });

export const ComponentHealthSchema = z.object({
  name: z.string(),
  status: z.enum(['ok', 'degraded', 'down', 'unknown']),
  detail: z.string().max(200).nullable(),
  checkedAt: IsoDateTimeSchema,
});
export const OpsHealthResponseSchema = z.object({
  ok: z.boolean(),
  version: z.string(),
  mode: z.enum(['local', 'cloud']),
  db: z.enum(['pglite', 'pg']),
  uptimeSeconds: CountSchema,
  components: z.array(ComponentHealthSchema),
  workerLastTickAt: IsoDateTimeSchema.nullable(),
  outboxPending: CountSchema,
  outboxDead: CountSchema,
  signingKeys: z.array(z.object({ purpose: z.enum(['entitlement', 'content']), kid: z.string(), createdAt: IsoDateTimeSchema })),
});
export type OpsHealthResponse = z.infer<typeof OpsHealthResponseSchema>;

export const AdminOverviewResponseSchema = z.object({
  access: z.object({ activeUsers: CountSchema, invitedUsers: CountSchema, suspendedUsers: CountSchema, activeDevices: CountSchema, devicesNeedingUpdate: CountSchema, activeSessions: CountSchema }),
  failures: z.object({ failedSyncs24h: CountSchema, rateLimitWaits24h: CountSchema, outboxDead: CountSchema, mailFailures: CountSchema, deniedActions24h: CountSchema, providerRevocationsPending: CountSchema }),
  usage: z.object({ requests24h: CountSchema, estimatedCostMicroUsd24h: CountSchema, unknownUsage24h: CountSchema, activeAssignments: CountSchema, usersWithoutAssignment: CountSchema }),
  auditDelivery: z.object({ reporters: CountSchema, staleReporters: CountSchema, openGaps: CountSchema, lastReceivedAt: IsoDateTimeSchema.nullable(), lastArchiveAt: IsoDateTimeSchema.nullable() }),
  content: z.object({ activeVersion: z.string().nullable(), rolloutStage: z.string().nullable(), adoptionCount: CountSchema }),
  generatedAt: IsoDateTimeSchema,
});
export type AdminOverviewResponse = z.infer<typeof AdminOverviewResponseSchema>;

const opsRead = ['device.manage', 'audit.read'] as const;

export const adminOpsRoutes = {
  'admin.overview': defineRoute({ id: 'admin.overview', method: 'GET', path: '/admin/v1/overview', auth: 'admin', requestSchema: NoQuerySchema, responseSchema: AdminOverviewResponseSchema, description: 'Overview: access, failures, usage, audit delivery. Sections the caller lacks capability for are zeroed server-side.' }),
  'admin.ops.health': defineRoute({ id: 'admin.ops.health', method: 'GET', path: '/admin/v1/ops/health', auth: 'admin', capability: opsRead, requestSchema: NoQuerySchema, responseSchema: OpsHealthResponseSchema, description: 'Component health, worker tick, outbox depth, signing keys.' }),
  'admin.ops.outbox.list': defineRoute({ id: 'admin.ops.outbox.list', method: 'GET', path: '/admin/v1/ops/outbox', auth: 'admin', capability: opsRead, requestSchema: OutboxListQuerySchema, responseSchema: OutboxPageSchema, description: 'Outbox items by kind/state.' }),
  'admin.ops.outbox.retry': defineRoute({ id: 'admin.ops.outbox.retry', method: 'POST', path: '/admin/v1/ops/outbox/:itemId/retry', auth: 'admin', capability: 'device.manage', paramsSchema: OutboxParamsSchema, requestSchema: EmptyRequestSchema, responseSchema: z.object({ item: OutboxItemSchema }), description: 'Reschedule a failed/dead outbox item.' }),
  'admin.ops.mail.list': defineRoute({ id: 'admin.ops.mail.list', method: 'GET', path: '/admin/v1/ops/mail', auth: 'admin', capability: opsRead, requestSchema: MailListQuerySchema, responseSchema: MailPageSchema, description: 'System mail deliveries (masked recipients).' }),
  'admin.ops.mail.retry': defineRoute({ id: 'admin.ops.mail.retry', method: 'POST', path: '/admin/v1/ops/mail/:deliveryId/retry', auth: 'admin', capability: 'device.manage', paramsSchema: MailParamsSchema, requestSchema: EmptyRequestSchema, responseSchema: z.object({ delivery: MailDeliverySchema }), description: 'Retry a failed delivery (deduplicated).' }),
  'admin.ops.mail.test': defineRoute({ id: 'admin.ops.mail.test', method: 'POST', path: '/admin/v1/ops/mail/test', auth: 'admin', capability: 'device.manage', requestSchema: EmptyRequestSchema, responseSchema: z.object({ delivery: MailDeliverySchema }), description: 'Queue a test message to the signed-in administrator: verifies the mail provider end to end.' }),
  'admin.ops.releasePolicy.get': defineRoute({ id: 'admin.ops.releasePolicy.get', method: 'GET', path: '/admin/v1/ops/release-policy', auth: 'admin', capability: opsRead, requestSchema: NoQuerySchema, responseSchema: ReleasePolicyResponseSchema, description: 'Release policy and cohorts.' }),
  'admin.ops.releasePolicy.set': defineRoute({ id: 'admin.ops.releasePolicy.set', method: 'PUT', path: '/admin/v1/ops/release-policy', auth: 'admin', capability: 'device.manage', recentAuth: true, requestSchema: ReleasePolicyUpdateRequestSchema, responseSchema: ReleasePolicyResponseSchema, description: 'Update minimum version, approved build ids and rollout stage (revision-checked).' }),
  'admin.ops.cohorts.set': defineRoute({ id: 'admin.ops.cohorts.set', method: 'PUT', path: '/admin/v1/ops/release-policy/cohorts', auth: 'admin', capability: 'device.manage', recentAuth: true, requestSchema: CohortsUpdateRequestSchema, responseSchema: ReleasePolicyResponseSchema, description: 'Replace named cohorts (3 / 10 / all).' }),
  'admin.ops.rateLimits.get': defineRoute({ id: 'admin.ops.rateLimits.get', method: 'GET', path: '/admin/v1/ops/rate-limits', auth: 'admin', capability: [...opsRead, 'template.edit', 'template.publish'], requestSchema: NoQuerySchema, responseSchema: RateLimitConfigSchema, description: 'Active rate-limit configuration.' }),
  'admin.ops.rateLimits.set': defineRoute({ id: 'admin.ops.rateLimits.set', method: 'PUT', path: '/admin/v1/ops/rate-limits', auth: 'admin', capability: 'template.publish', recentAuth: true, requestSchema: RateLimitConfigUpdateRequestSchema, responseSchema: RateLimitConfigSchema, description: 'Publish a new rate-limit configuration version within validated bounds.' }),
  'admin.ops.featureFlags.get': defineRoute({ id: 'admin.ops.featureFlags.get', method: 'GET', path: '/admin/v1/ops/feature-flags', auth: 'admin', capability: [...opsRead, 'template.edit', 'template.publish'], requestSchema: NoQuerySchema, responseSchema: FeatureFlagsSchema, description: 'Active feature flags and per-account Rumi disablement.' }),
  'admin.ops.featureFlags.set': defineRoute({ id: 'admin.ops.featureFlags.set', method: 'PUT', path: '/admin/v1/ops/feature-flags', auth: 'admin', capability: 'template.publish', recentAuth: true, requestSchema: FeatureFlagsUpdateRequestSchema, responseSchema: FeatureFlagsSchema, description: 'Publish a new feature-flag version.' }),
  'admin.ops.modelConfig.get': defineRoute({ id: 'admin.ops.modelConfig.get', method: 'GET', path: '/admin/v1/ops/model-config', auth: 'admin', capability: [...opsRead, 'template.edit', 'template.publish', 'ai_key.manage'], requestSchema: NoQuerySchema, responseSchema: ModelConfigSchema, description: 'Active model configuration and price table.' }),
  'admin.ops.modelConfig.set': defineRoute({ id: 'admin.ops.modelConfig.set', method: 'PUT', path: '/admin/v1/ops/model-config', auth: 'admin', capability: 'template.publish', recentAuth: true, requestSchema: ModelConfigUpdateRequestSchema, responseSchema: ModelConfigSchema, description: 'Publish a new model configuration version.' }),
  'admin.ops.leases.list': defineRoute({ id: 'admin.ops.leases.list', method: 'GET', path: '/admin/v1/ops/leases', auth: 'admin', capability: opsRead, requestSchema: LeaseListQuerySchema, responseSchema: LeasePageSchema, description: 'Provider coordination leases.' }),
  'admin.ops.leases.release': defineRoute({ id: 'admin.ops.leases.release', method: 'POST', path: '/admin/v1/ops/leases/:leaseId/release', auth: 'admin', capability: 'device.manage', recentAuth: true, paramsSchema: LeaseParamsSchema, requestSchema: EmptyRequestSchema, responseSchema: z.object({ lease: ProviderLeaseSchema }), description: 'Force-release a stuck lease (emits ops.lease_released).' }),
} as const;
